import { formatISO, isBefore, isValid, subDays } from 'date-fns';

import { catchErrorSync } from '../app/components/Common/AppErrorBoundary';
import i18n from '../app/locales/i18n';
import Helper from '../app/modules/Helper';
import store from '../store';
import { getFbClientId } from '../utils/cookies';
import { emitSearchWithoutLocationGAEvent } from '../utils/ga-event-helpers';
import {
	emitSearchTrackingEvents,
	hideAndShowErrors,
	sendDataToCompare,
	sendDataToPassthruForm,
	setTicketData,
	setTooltipError,
	submitForm,
	togglePriceAlertSignUpModal,
	toggleProgressLoading,
} from './baseActions';
import { saveFrontFormDataToSessionStorage } from './baseActions.utils';
import { setErrorForPriceAlertEmail, setErrorForPriceAlertPhone } from './error-checks';
import {
	FLIGHT_ADD_HOTELS,
	FLIGHT_SELECT_FROM_DATE,
	FLIGHT_SELECT_FROM_LOCATION,
	FLIGHT_SELECT_PRICING_TYPE,
	FLIGHT_SELECT_TO_DATE,
	FLIGHT_SELECT_TO_LOCATION,
	FLIGHT_SELECT_TRIP_TYPE,
	FLIGHT_SET_COUNT_ADULTS,
	FLIGHT_SET_COUNT_CHILDREN,
} from './types';

import { TripType, pricingTypes } from 'app/modules/Constants';

export const selectFromDate = (date) =>
	catchErrorSync((dispatch) => {
		if (!isValid(date) || isBefore(date, subDays(new Date(), 1))) {
			return;
		}
		if (date.getTime() > store.getState().flight.selectedToDate.getTime()) {
			store.dispatch(selectToDate(date));
		}
		dispatch({
			type: FLIGHT_SELECT_FROM_DATE,
			payload: date,
		});
	});

export const selectToDate = (date) =>
	catchErrorSync((dispatch) => {
		if (!isValid(date) || isBefore(date, subDays(new Date(), 1))) {
			return;
		}
		dispatch({
			type: FLIGHT_SELECT_TO_DATE,
			payload: date,
		});
	});

export const selectFromLocation = (location) =>
	catchErrorSync((dispatch) => {
		if (location.iata === store.getState().flight.selectedFromLocation.iata) {
			return;
		}
		dispatch({
			type: FLIGHT_SELECT_FROM_LOCATION,
			payload: location,
		});

		const input = document.getElementById('airport-to-input');
		if (!!input && !Helper.isEmpty(location) && !store.getState().base.preventFocus) {
			input.focus();
			input.select();
		}
		const errors = { ...store.getState().base.tooltipErrors };
		delete errors['airport-from'];
		store.dispatch(setTooltipError(errors));
	});

export const selectToLocation = (location) =>
	catchErrorSync((dispatch) => {
		if (location.iata === store.getState().flight.selectedToLocation.iata) {
			return;
		}
		dispatch({
			type: FLIGHT_SELECT_TO_LOCATION,
			payload: location,
		});
		const errors = { ...store.getState().base.tooltipErrors };
		delete errors['airport-to'];
		store.dispatch(setTooltipError(errors));
	});

export const selectTripType = (tripType) =>
	catchErrorSync((dispatch) => {
		dispatch({
			type: FLIGHT_SELECT_TRIP_TYPE,
			payload: tripType,
		});
	});

export const selectPricingType = (pricingType) =>
	catchErrorSync((dispatch) => {
		dispatch({
			type: FLIGHT_SELECT_PRICING_TYPE,
			payload: pricingType,
		});
	});

export const setCountAdults = (count) =>
	catchErrorSync((dispatch) => {
		count = parseInt(count);
		if (count >= 1 && count <= 9) {
			dispatch({
				type: FLIGHT_SET_COUNT_ADULTS,
				payload: count,
			});
		}
	});

export const setCountChildren = (count) =>
	catchErrorSync((dispatch) => {
		count = parseInt(count);
		if (count >= 0 && count <= 9) {
			dispatch({
				type: FLIGHT_SET_COUNT_CHILDREN,
				payload: count,
			});
		}
	});

export const doSearchFlights = (compare) =>
	catchErrorSync(() => {
		compare = compare || false;
		const flightState = store.getState().flight;
		const baseState = store.getState().base;
		const errors = {};
		const { selectedFromLocation, selectedToLocation, addHotelsChecked } = flightState;

		setErrorForPriceAlertEmail(errors);
		setErrorForPriceAlertPhone(errors);

		if (addHotelsChecked) {
			const url = new URL(window.location.href);
			const bParam = url.searchParams.get('b');
			Helper.setOrAppendParam(url, 'bvert', 'hotels');
			Helper.setOrAppendParam(url, 'b', bParam || 'b');
			window.history.pushState({}, '', url.toString());
		} else {
			const url = new URL(window.location.href);
			if (url.searchParams.has('bvert') && url.searchParams.get('bvert') === 'hotels') {
				Helper.removeParam(url, 'bvert');
				window.history.pushState({}, '', url.toString());
				if (url.searchParams.has('b') && url.searchParams.get('b') === 'b') {
					Helper.removeParam(url, 'b');
					window.history.pushState({}, '', url.toString());
				}
			}
		}

		if (!Object.keys(selectedFromLocation).length) {
			emitSearchWithoutLocationGAEvent('flights');
			errors['airport-from'] = i18n.t('validation.flights.fromLocation');
		}
		if (!Object.keys(selectedToLocation).length) {
			if (Object.keys(selectedFromLocation).length) {
				emitSearchWithoutLocationGAEvent('flights');
			}
			errors['airport-to'] = i18n.t('validation.flights.toLocation');
		}

		if (selectedFromLocation.iata && selectedToLocation.iata) {
			if (selectedFromLocation.iata === selectedToLocation.iata) {
				errors['airport-to'] = i18n.t('validation.flights.sameLocations');
			}
		}
		store.dispatch(setTooltipError(errors));
		if (
			Helper.isEmpty(errors) &&
			!baseState.isClosedPriceAlertSignUpModal &&
			baseState.priceAlertHandlerChecked &&
			Helper.isDesktop()
		) {
			store.dispatch(togglePriceAlertSignUpModal(true));
			return;
		}
		if (Helper.isEmpty(errors)) {
			store.dispatch(emitSearchTrackingEvents(store.getState().base.enabledVert));
			const parsed = Helper.parseQueryString(window.location.search, true);
			let preventOpenFrontTab = false;
			if (Helper.hasProperty(parsed, 'f') && parsed.f === '0') {
				preventOpenFrontTab = true;
			}
			if (Helper.hasProperty(parsed, 'autoredirect') && parsed.autoredirect === '1') {
				preventOpenFrontTab = true;
			}
			let openBackTabTimeout = 500;
			if (preventOpenFrontTab) {
				openBackTabTimeout = 100;
			}

			setTimeout(() => {
				saveFrontFormDataToSessionStorage(store.dispatch(getFormData('front')));
				if (compare) {
					store.dispatch(sendDataToCompare(store.dispatch(getFormData('front'))));
				} else if (!preventOpenFrontTab) {
					store.dispatch(submitFormFront());
				}

				if (!compare) {
					setTimeout(() => {
						store.dispatch(submitFormBack());
					}, openBackTabTimeout);
				}
			}, 10);
		} else {
			store.dispatch(hideAndShowErrors());
		}
	});

export const submitFormBack = () =>
	catchErrorSync(() => {
		const form = document.getElementById('redirect-back-form');
		const data = store.dispatch(getFormData('back'));
		store.dispatch(setTicketData(data));
		store.dispatch(toggleProgressLoading(true));
		store.dispatch(submitForm(form, data));
	});

export const submitFormFront = () =>
	catchErrorSync(() => {
		const data = store.dispatch(getFormData('front'));
		store.dispatch(sendDataToPassthruForm(data));
	});

export const setAddHotels = (checked) =>
	catchErrorSync((dispatch) => {
		dispatch({
			type: FLIGHT_ADD_HOTELS,
			payload: checked,
		});
	});

export const getFormData = (formType) =>
	catchErrorSync(() => {
		const data = store.getState().flight;
		const from = data.selectedFromLocation;
		const to = data.selectedToLocation;
		const dateFrom = data.selectedFromDate;
		const dateTo = data.selectedToDate;
		const tripType = data.selectedTripType;
		const pricingType = data.selectedPricingType;
		const countAdults = data.countAdults;
		const countChildren = data.countChildren;
		const vert = store.getState().base.enabledVert;
		const isOneWay = tripType.value === TripType.ONE_WAY;

		const date_from = dateFrom;
		const date_to = dateTo;

		const diffTime = Math.abs(date_to - date_from);
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		const dta = Math.abs(date_from - Date.now());
		const dtaDiff = Math.ceil(dta / (1000 * 60 * 60 * 24));
		const fbp = getFbClientId();

		const formData = {
			tab: formType,
			vert,
			'origin-iata': from.iata,
			'destination-iata': to.iata,
			'departure-time': formatISO(dateFrom),
			'cabin-class': pricingType.value,
			'num-adults': countAdults,
			'num-youth': countChildren,
			...(!isOneWay && { 'return-time': formatISO(dateTo) }),
			...(!isOneWay && { 'rental-duration': diffDays }),
			dta: dtaDiff,
			...(fbp && { fbp }),
		};
		if (formType === 'back') {
			const email = store.getState().base.priceAlertEmail;
			const phone = store.getState().base.priceAlertPhone;

			if (email) {
				formData['email'] = email;
			}
			if (phone) {
				formData['phone'] = phone;
			}
		}

		return formData;
	});

export const prePopulateDataFromQueryString = () =>
	catchErrorSync(() => {
		const parsed = Helper.parseQueryString(window.location.search, true);
		if (Helper.hasProperty(parsed, 'date-from') && parsed['date-from']) {
			let dateFrom = parsed['date-from'];
			if (Helper.isValidDateFormat(dateFrom)) {
				dateFrom = Helper.convertStringDateToDateObject(dateFrom);
				store.dispatch(selectFromDate(dateFrom));
			}
		}
		if (Helper.hasProperty(parsed, 'date-to') && parsed['date-to']) {
			let dateTo = parsed['date-to'];
			if (Helper.isValidDateFormat(dateTo)) {
				dateTo = Helper.convertStringDateToDateObject(dateTo);
				store.dispatch(selectToDate(dateTo));
			}
		}
		if (Helper.hasProperty(parsed, 'cabin') && parsed['cabin']) {
			const cabin = parsed['cabin'];
			const pricingType = pricingTypes.find((type) => type.value.startsWith(cabin));

			if (pricingType) {
				store.dispatch(selectPricingType(pricingType));
			}
		}
		if (Helper.hasProperty(parsed, 'bvert') && Helper.hasProperty(parsed, 'b')) {
			store.dispatch(setAddHotels(true));
		}
	});
